import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { User, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PlaybookMarker from '@/components/PlaybookMarker';
import PursuitMarker from '@/components/PursuitMarker';  // ADD THIS
import { PlaybookStatusProvider } from '@/components/PlaybookStatusContext';
import { useTranslation } from '@/components/utils/translations';
import PersonAvatar from '@/components/PersonAvatar';
import { sanitizeId } from '@/components/utils/id';
import { personaFitBand } from '@/lib/personaFitLabel';

const PERSONA_STYLES = {
  strategic: { color: "bg-green-100 text-green-800", circleColor: "bg-green-500" },
  supportive: { color: "bg-blue-100 text-blue-800", circleColor: "bg-blue-500" },
  passive: { color: "bg-yellow-100 text-yellow-800", circleColor: "bg-yellow-500" },
  lowFit: { color: "bg-red-100 text-red-800", circleColor: "bg-red-500" },
};
const getPersonaLabel = (score, t) => {
  const band = personaFitBand(score) ?? 'passive';
  return { label: t(band), ...PERSONA_STYLES[band] };
};

export default function TopPersonas({ personas, isLoading, currentTenant }) {
  const { t } = useTranslation();
  const personIds = personas.slice(0, 5).map(p => p.person_id || p.id).filter(Boolean);
  
  return (
    <PlaybookStatusProvider personIds={personIds} currentTenant={currentTenant}>
      <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#00C2C2]" />
                <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('topPersonas')}</CardTitle>
            </div>
             <Link to={createPageUrl('People')} className="text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">{t('viewAll')}</Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {isLoading ? (
            Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
          ) : personas.slice(0, 5).map(p => {
              const personaFit = getPersonaLabel(p.persona_fit_score, t);
              const fullName = `${p.first_name} ${p.last_name}`;
              
              const idForProfileLink = sanitizeId(p.person_id || p.id);
              
              if (!idForProfileLink) {
                return (
                  <div key={p.id} className="p-3 rounded-lg bg-gray-50/70 opacity-50 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                    <PersonAvatar
                          personId={p.person_id || p.id}
                          hostedPhotoUrl={p.hosted_photo_url}
                          linkedinPhotoUrl={p.profile_photo_url}
                          firstName={p.first_name}
                          lastName={p.last_name}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#0A1A2F] truncate">{fullName}</p>
                            <p className="text-xs text-[#5C6B7A] truncate">{p.role_title}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <Badge className={`${personaFit.color} text-xs whitespace-nowrap px-2`}>{personaFit.label} ({p.persona_fit_score})</Badge>
                        <span className="text-xs text-gray-400">No profile</span>
                    </div>
                  </div>
                );
              }
              
              return (
                <Link key={p.id || p.person_id} to={createPageUrl(`PersonProfile?id=${encodeURIComponent(idForProfileLink)}`)} className="p-3 rounded-lg bg-gray-50/70 hover:bg-[#00C2C2]/10 transition-colors flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                    <PersonAvatar
                          personId={p.person_id || p.id}
                          hostedPhotoUrl={p.hosted_photo_url}
                          linkedinPhotoUrl={p.profile_photo_url}
                          firstName={p.first_name}
                          lastName={p.last_name}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <p className="font-semibold text-[#0A1A2F] truncate">{fullName}</p>
                                <PlaybookMarker personId={p.person_id || p.id} />
                                <PursuitMarker isInPursuit={p.add_to_pursuit} />
                            </div>
                            <p className="text-xs text-[#5C6B7A] truncate">{p.role_title}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* ADDED: Tooltip for persona fit score with rationale */}
                        {p.persona_score_rationale ? (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button 
                                  className="touch-manipulation"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <Badge className={`${personaFit.color} text-xs whitespace-nowrap px-2 cursor-help`}>
                                    {personaFit.label} ({p.persona_fit_score})
                                  </Badge>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent 
                                side="left" 
                                align="center"
                                sideOffset={8}
                                className="w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                              >
                                <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('personaFitRationale')}</p>
                                <p className="text-sm text-white leading-relaxed">{p.persona_score_rationale}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Badge className={`${personaFit.color} text-xs whitespace-nowrap px-2`}>
                            {personaFit.label} ({p.persona_fit_score})
                          </Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400 hidden md:block" />
                    </div>
                </Link>
              );
          })}
        </div>
      </CardContent>
    </Card>
    </PlaybookStatusProvider>
  );
}